import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { useLocation, useNavigate } from "react-router-dom";
import { checkVideoAvailability } from "../../Repository/Api";
import {
  buildWatchUrl,
  enrichVideosWithTimestamps,
  videosFromCamiListRow,
} from "../../Repository/CamiCodeVideosApi";

function formatDate(value) {
  if (!value) return null;
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return String(value);
  return new Date(parsed).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDuration(seconds) {
  if (seconds == null || seconds === "") return null;
  const total = Number(seconds);
  if (!Number.isFinite(total) || total <= 0) return null;
  const mins = Math.floor(total / 60);
  const secs = Math.floor(total % 60);
  if (mins > 0) {
    return `${mins}:${String(secs).padStart(2, "0")}`;
  }
  return `${secs}s`;
}



export default function QrCodePreviousVideos() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const navy = theme.palette.primary.main;
  const accent = theme.palette.secondary.main;

  const query = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      camiCode: (params.get("c") || "").replace(/ /g, ""),
      uniqueId: (params.get("p") || "").replace(/ /g, ""),
      currentVideoId: (params.get("v") || "").replace(/ /g, ""),
      redir: params.get("redir") || "",
      email: (params.get("e") || "").replace(/ /g, ""),
    };
  }, [location.search]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    if (!query.camiCode || !query.uniqueId) {
      setLoading(false);
      setError("Missing QR code details. Scan or open a valid link.");
      return;
    }

    let cancelled = false;

    async function loadVideos() {
      setLoading(true);
      setError("");

      try {
        const result = await checkVideoAvailability(query.camiCode, query.uniqueId);
        if (cancelled) {
          return;
        }

        let list = [...videosFromCamiListRow(result)];
        console.log("video list",list)

        list = await enrichVideosWithTimestamps(list);
        list.sort((a, b) => {
          const ta = Date.parse(a?.createdAt) || 0;
          const tb = Date.parse(b?.createdAt) || 0;
          return tb - ta;
        });

        if (!cancelled) {
          setVideos(list);
        }
      } catch {
        if (!cancelled) {
          setVideos([]);
          setError("Unable to load videos for this QR code.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadVideos();

    return () => {
      cancelled = true;
    };
  }, [query.camiCode, query.uniqueId]);

  const handlePlay = (video) => {
    if (!video?.videoId) return;
    const watchUrl = buildWatchUrl({
      camiCode: query.camiCode,
      uniqueId: query.uniqueId,
      videoId: video.videoId,
      redir: query.redir || encodeURIComponent("/previous-videos"),
      email: query.email || undefined,
      playConfirmed: true,
    });
    navigate(watchUrl);
  };

  const latestVideoId = useMemo(() => {
    if (!videos.length) return null;

    return videos.reduce((latest, current) => {
      if (!latest) return current;
      const latestDate = new Date(latest.createdAt || 0).getTime();
      const currentDate = new Date(current.createdAt || 0).getTime();
      return currentDate > latestDate ? current : latest;
    }, null)?.videoId;
  }, [videos]);

  return (
    <Box
      sx={{
        flex: 1,
        width: "100%",
        minHeight: "100dvh",
        bgcolor: "#F8F6F2",
        py: { xs: 3, md: 4 },
        px: { xs: 2, sm: 3 },
      }}
    >
      <Box sx={{ maxWidth: 640, mx: "auto" }}>
        <Typography
          component="h1"
          sx={{
            color: navy,
            fontWeight: 800,
            fontSize: { xs: "1.5rem", md: "1.75rem" },
            letterSpacing: "-0.02em",
            mb: 0.75,
          }}
        >
          All Videos
        </Typography>
        <Typography sx={{ color: theme.palette.text.secondary, mb: 3, lineHeight: 1.5 }}>
          All videos for this QR code
          {query.camiCode ? ` (${query.camiCode})` : ""}.
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: accent }} />
          </Box>
        ) : error ? (
          <Typography sx={{ color: accent, py: 4, textAlign: "center" }}>{error}</Typography>
        ) : videos.length === 0 ? (
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: `1px solid ${theme.palette.divider}`,
              textAlign: "center",
              py: 6,
              px: 2,
            }}
          >
            <Typography sx={{ color: theme.palette.text.secondary }}>
              No videos found for this QR code yet.
            </Typography>
          </Card>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {videos.map((video, index) => {
              const isLatest = video.videoId === latestVideoId;

              const dateLabel = formatDate(video.createdAt);
              const durationLabel = formatDuration(video.duration);
              const meta = [dateLabel, durationLabel].filter(Boolean).join(" · ");

              return (
                <Card
                  key={video.id || video.videoId || index}
                  elevation={0}
                  onClick={() => handlePlay(video)}
                  sx={{
                    borderRadius: "16px",
                    border: `2px solid ${isLatest ? accent : theme.palette.divider
                      }`,
                    bgcolor: isLatest ? "#FFF8F3" : "#fff",
                    boxShadow: isLatest
                      ? "0 8px 24px rgba(176, 24, 64, 0.18)"
                      : "0 2px 8px rgba(24, 32, 104, 0.05)",
                    cursor: "pointer",
                    transition:
                      "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease",
                    "&:hover": {
                      borderColor: accent,
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 24px rgba(24, 32, 104, 0.10)",
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      py: 1.75,
                      "&:last-child": { pb: 1.75 },
                    }}
                  >
                    <PlayCircleOutlineIcon
                      sx={{
                        color: isLatest ? accent : navy,
                        fontSize: "2rem",
                        flexShrink: 0,
                      }}
                    />

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "1rem",
                            color: navy,
                            lineHeight: 1.3,
                          }}
                        >
                          {video.title || `Video ${index + 1}`}
                        </Typography>

                        {isLatest && (
                          <Chip
                            label="Latest Video"
                            size="small"
                            sx={{
                              bgcolor: accent,
                              color: "#fff",
                              fontWeight: 600,
                              height: 22,
                              fontSize: "0.7rem",
                            }}
                          />
                        )}
                      </Box>

                      {meta && (
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            color: theme.palette.text.secondary,
                            mt: 0.35,
                          }}
                        >
                          {meta}
                        </Typography>
                      )}
                    </Box>

                    <Typography
                      sx={{
                        color: accent,
                        fontWeight: 700,
                        fontSize: "0.9rem",
                        flexShrink: 0,
                      }}
                    >
                      Watch
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}
